/**
 * Preset CRUD section setup: section 01 of the controls sidebar.
 *
 * Builds the TONE PRESET section containing the preset select and Save / Manage
 * controls. Extracted from createControls (CSS-04.5): public API unchanged.
 */

import { onLocaleChange, t } from "../../i18n";
import {
  createInitialState,
  effectiveDialValues,
  type DemoState,
  type MethodSnapshot,
  type PatternAssetId,
  type StampMaskAssetId,
  type Store,
} from "../../state";
import { TONE_PRESETS } from "../tone-presets";
import {
  loadUserPresets,
  saveUserPresets,
  snapshotPresetPatch,
  validateImportedPreset,
  downloadPresetAsBundle,
  generateId,
  methodSnapshotToPresetPatch,
  embeddedAssetsForMethodSnapshot,
  mergeEmbeddedAssetsIntoState,
  binaryPatchForMethodSnapshot,
  type UserPreset,
} from "../user-presets";
import { applyMethodPartial, type MethodPartial } from "../../util/method-patch";
import { buildMethodSnapshot } from "../../util/method-patch";
import { pushUndoSnapshot } from "../../util/region-undo";
import { registerAsset } from "../../util/asset-registry";
import { makeCustomSelect } from "../custom-select";
import { decodePresetBundle } from "../../persistence/bundles";

interface SectionGroup {
  section: HTMLElement;
  body: HTMLDivElement;
  head: HTMLHeadingElement;
}

export interface PresetCrudDeps {
  store: Store;
  root: HTMLElement;
  makeSection: (
    ix: string,
    titleKey: string,
    variant?: "" | "violet" | "yellow",
  ) => SectionGroup;
  makeRow: () => HTMLDivElement;
  makeLabelText: (
    labelKey: string,
    tooltipKey: string | undefined,
    htmlFor: string,
  ) => HTMLSpanElement;
}

export interface PresetCrudRefs {
  presetSelect: HTMLSelectElement;
  syncExportEnabled: () => void;
}

export function setupPresetCrud(deps: PresetCrudDeps): PresetCrudRefs {
  const { store, root, makeSection, makeRow, makeLabelText } = deps;

  // ========== 01 TONE PRESET (3.1 + C1) ==========
  // Preset dropdown: quick-apply a named tone configuration. 'custom' is the
  // sentinel shown when the user has diverged from any named preset.
  // C1: user-saved presets appear below a divider; Save and Manage actions.

  const tonePresetSection = makeSection("01", "section.tonePreset", "violet");
  root.appendChild(tonePresetSection.section);

  const tonePresetTitle = tonePresetSection.head.querySelector<HTMLElement>(
    '[data-i18n="section.tonePreset"]',
  );
  function syncTonePresetTitle(): void {
    if (tonePresetTitle) tonePresetTitle.textContent = t("section.tonePreset");
  }
  syncTonePresetTitle();
  onLocaleChange(syncTonePresetTitle);

  function hasMethodSelectionTarget(state: DemoState): boolean {
    if (state.activeStackUuid !== null) {
      return state.regionStacks.some(
        (stack) => stack.uuid === state.activeStackUuid,
      );
    }
    if (state.activeRegionUuids.length === 0) return false;
    const entryUuids = new Set(state.regionEntries.map((entry) => entry.uuid));
    return state.activeRegionUuids.some((uuid) => entryUuids.has(uuid));
  }

  function applyTonePresetPatch(id: string, patch: Partial<DemoState>): void {
    const state = store.get();
    const nextUndoStack = pushUndoSnapshot(state);
    if (hasMethodSelectionTarget(state)) {
      store.patch({
        ...applyMethodPartial(state, patch as MethodPartial),
        tonePreset: "custom",
        regionUndoStack: nextUndoStack,
      });
      return;
    }
    store.patch({
      tonePreset: id,
      regionUndoStack: nextUndoStack,
      ...patch,
    });
  }

  // User preset state - loaded from localStorage at init, kept in sync.
  let userPresets: UserPreset[] = loadUserPresets();

  const presetRow = makeRow();
  presetRow.className = "row tone-preset-row";

  // ── Label - uses canonical row-label wrapper (same pattern as all other rows) ───
  const presetLabelWrap = makeLabelText("label.tonePreset", undefined, "ctrl-tonePreset");
  presetLabelWrap.classList.add("row-label--sr-only");

  // ── Native <select> (sr-only, always in DOM for test access) ──────────────
  const presetSelect = document.createElement("select");
  presetSelect.id = "ctrl-tonePreset";
  presetSelect.className = "row-select";

  let syncExportEnabled: () => void = () => {};

  function rebuildPresetOptions(): void {
    presetSelect.replaceChildren();
    // Built-in presets.
    for (const preset of TONE_PRESETS) {
      const opt = document.createElement("option");
      opt.value = preset.id;
      opt.dataset["i18n"] = preset.nameKey;
      opt.textContent = t(preset.nameKey);
      presetSelect.appendChild(opt);
    }
    // Divider + user presets.
    if (userPresets.length > 0) {
      const divider = document.createElement("option");
      divider.disabled = true;
      divider.textContent = t("label.tonePreset.user.divider");
      presetSelect.appendChild(divider);
      for (const up of userPresets) {
        const opt = document.createElement("option");
        opt.value = up.id;
        opt.textContent = up.name;
        presetSelect.appendChild(opt);
      }
    }
    presetSelect.value = store.get().tonePreset;
    presetCselect.refresh();
    syncExportEnabled();
    syncManagePopup();
  }

  presetRow.appendChild(presetLabelWrap);
  presetRow.appendChild(presetSelect);
  const presetCselect = makeCustomSelect(presetSelect);
  presetRow.appendChild(presetCselect.root);

  // ── Action button row (Save / Manage) ─────────────────────────────────────
  const presetActionsRow = document.createElement("div");
  presetActionsRow.className = "tone-preset-actions";

  // Save button.
  const saveBtn = document.createElement("button");
  saveBtn.type = "button";
  saveBtn.className = "btn tone-preset-btn";
  saveBtn.dataset["i18n"] = "label.tonePreset.save";
  saveBtn.textContent = t("label.tonePreset.save");

  const manageBtn = document.createElement("button");
  manageBtn.type = "button";
  manageBtn.className = "btn tone-preset-btn";
  manageBtn.dataset["i18n"] = "label.tonePreset.manage";
  manageBtn.textContent = t("label.tonePreset.manage");
  manageBtn.setAttribute("aria-haspopup", "true");
  manageBtn.setAttribute("aria-expanded", "false");

  // Inline name input (hidden until Save is clicked).
  // Given a ctrl- id and a visually-hidden label so accessibility tests pass.
  const saveNameLabel = document.createElement("label");
  saveNameLabel.htmlFor = "ctrl-tonePresetName";
  saveNameLabel.className = "sr-only";
  saveNameLabel.textContent = t("label.tonePreset.save");
  presetActionsRow.appendChild(saveNameLabel);

  const saveNameInput = document.createElement("input");
  saveNameInput.type = "text";
  saveNameInput.id = "ctrl-tonePresetName";
  saveNameInput.className = "tone-preset-name-input";
  saveNameInput.placeholder = t("label.tonePreset.name.placeholder");
  saveNameInput.hidden = true;

  // Status text (saved / error).
  const saveStatusEl = document.createElement("span");
  saveStatusEl.className = "tone-preset-status";
  saveStatusEl.hidden = true;

  function showPresetStatus(key: string, durationMs = 2000): void {
    saveStatusEl.textContent = t(key);
    saveStatusEl.hidden = false;
    setTimeout(() => {
      saveStatusEl.hidden = true;
    }, durationMs);
  }

  saveBtn.addEventListener("click", () => {
    const isOpen = !saveNameInput.hidden;
    if (isOpen) {
      saveNameInput.hidden = true;
      return;
    }
    saveNameInput.hidden = false;
    saveNameInput.value = "";
    saveNameInput.focus();
  });

  function commitSavePreset(): void {
    const name = saveNameInput.value.trim();
    if (!name) return;
    const patch = snapshotEffectivePresetPatch();
    const newPreset: UserPreset = { id: generateId(), name, patch };
    userPresets = [...userPresets, newPreset];
    saveUserPresets(userPresets);
    store.patch({ tonePreset: newPreset.id });
    rebuildPresetOptions();
    saveNameInput.hidden = true;
    saveNameInput.value = "";
    showPresetStatus("status.tonePreset.saved");
  }

  saveNameInput.addEventListener("keydown", (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      commitSavePreset();
    }
    if (e.key === "Escape") {
      saveNameInput.hidden = true;
    }
  });
  saveNameInput.addEventListener("blur", () => {
    // Delay so clicking Save again registers before blur closes the input.
    setTimeout(() => {
      if (saveNameInput.hidden) return;
      if (saveNameInput.value.trim()) {
        commitSavePreset();
      } else {
        saveNameInput.hidden = true;
      }
    }, 150);
  });

  function findPresetById(id: string): UserPreset | null {
    const userPreset = userPresets.find((p) => p.id === id);
    if (userPreset) return userPreset;

    const builtInPreset = TONE_PRESETS.find((p) => p.id === id);
    if (!builtInPreset?.patch || builtInPreset.id === "custom") return null;
    return {
      id: builtInPreset.id,
      name: t(builtInPreset.nameKey),
      patch: builtInPreset.patch,
    };
  }

  const managePopup = document.createElement("div");
  managePopup.className = "settings-popup tone-preset-manage-popup";
  managePopup.setAttribute("role", "dialog");
  managePopup.hidden = true;
  document.body.appendChild(managePopup);

  const manageCurrentSection = document.createElement("div");
  manageCurrentSection.className = "settings-popup-section";

  const manageCurrentLabel = document.createElement("div");
  manageCurrentLabel.className = "settings-popup-label";
  manageCurrentLabel.dataset["i18n"] = "label.tonePreset.current";
  manageCurrentLabel.textContent = t("label.tonePreset.current");

  const manageCurrentName = document.createElement("div");
  manageCurrentName.className = "tone-preset-current-name";

  manageCurrentSection.appendChild(manageCurrentLabel);
  manageCurrentSection.appendChild(manageCurrentName);
  managePopup.appendChild(manageCurrentSection);

  const manageRenameSection = document.createElement("div");
  manageRenameSection.className = "settings-popup-section";

  const renameLabel = document.createElement("label");
  renameLabel.className = "settings-popup-label";
  renameLabel.htmlFor = "ctrl-tonePresetRename";
  renameLabel.dataset["i18n"] = "label.tonePreset.rename";
  renameLabel.textContent = t("label.tonePreset.rename");

  const renameInput = document.createElement("input");
  renameInput.type = "text";
  renameInput.id = "ctrl-tonePresetRename";
  renameInput.className = "tone-preset-name-input";
  renameInput.placeholder = t("label.tonePreset.name.placeholder");

  const renameBtn = document.createElement("button");
  renameBtn.type = "button";
  renameBtn.className = "btn tone-preset-btn";
  renameBtn.dataset["i18n"] = "action.tonePreset.rename";
  renameBtn.textContent = t("action.tonePreset.rename");

  manageRenameSection.appendChild(renameLabel);
  manageRenameSection.appendChild(renameInput);
  manageRenameSection.appendChild(renameBtn);
  managePopup.appendChild(manageRenameSection);

  const manageActionSection = document.createElement("div");
  manageActionSection.className =
    "settings-popup-section tone-preset-manage-actions";
  managePopup.appendChild(manageActionSection);

  const deleteBtn = document.createElement("button");
  deleteBtn.type = "button";
  deleteBtn.className = "btn tone-preset-btn";
  deleteBtn.dataset["i18n"] = "action.tonePreset.delete";
  deleteBtn.textContent = t("action.tonePreset.delete");

  let managePopupOpen = false;

  function currentUserPreset(): UserPreset | null {
    return userPresets.find((p) => p.id === store.get().tonePreset) ?? null;
  }

  function currentPresetName(): string {
    if (store.get().tonePreset === "custom") return t("preset.custom.name");
    return findPresetById(store.get().tonePreset)?.name ?? t("preset.custom.name");
  }

  function syncManagePopup(): void {
    const userPreset = currentUserPreset();
    manageCurrentName.textContent = currentPresetName();
    const canMutate = userPreset !== null;
    renameInput.disabled = !canMutate;
    renameBtn.disabled = !canMutate;
    deleteBtn.disabled = !canMutate;
    if (document.activeElement !== renameInput) {
      renameInput.value = userPreset?.name ?? "";
    }
  }

  function positionManagePopup(): void {
    const rect = manageBtn.getBoundingClientRect();
    const popupWidth = managePopup.offsetWidth || 220;
    const viewportWidth = window.innerWidth;
    let left = rect.left;
    if (left + popupWidth > viewportWidth - 8) {
      left = viewportWidth - popupWidth - 8;
    }
    if (left < 8) left = 8;
    managePopup.style.top = `${rect.bottom + 5}px`;
    managePopup.style.left = `${left}px`;
  }

  function openManagePopup(): void {
    managePopupOpen = true;
    managePopup.hidden = false;
    manageBtn.setAttribute("aria-expanded", "true");
    syncManagePopup();
    positionManagePopup();
  }

  function closeManagePopup(): void {
    if (!managePopupOpen) return;
    managePopupOpen = false;
    managePopup.hidden = true;
    manageBtn.setAttribute("aria-expanded", "false");
  }

  manageBtn.addEventListener("click", (event) => {
    event.stopPropagation();
    if (managePopupOpen) {
      closeManagePopup();
      return;
    }
    openManagePopup();
  });

  document.addEventListener("click", (event) => {
    const target = event.target;
    if (!(target instanceof Node)) return;
    if (managePopup.contains(target) || manageBtn.contains(target)) return;
    closeManagePopup();
  });

  document.addEventListener("keydown", (event) => {
    if (event.key === "Escape") closeManagePopup();
  });

  window.addEventListener("resize", () => {
    if (managePopupOpen) positionManagePopup();
  });
  window.addEventListener(
    "scroll",
    () => {
      if (managePopupOpen) positionManagePopup();
    },
    true,
  );

  function commitRenamePreset(): void {
    const userPreset = currentUserPreset();
    const nextName = renameInput.value.trim();
    if (!userPreset || !nextName) return;
    userPresets = userPresets.map((preset) =>
      preset.id === userPreset.id ? { ...preset, name: nextName } : preset,
    );
    saveUserPresets(userPresets);
    rebuildPresetOptions();
    syncManagePopup();
    showPresetStatus("status.tonePreset.saved");
  }

  renameBtn.addEventListener("click", commitRenamePreset);
  renameInput.addEventListener("keydown", (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      commitRenamePreset();
    }
    if (event.key === "Escape") {
      event.preventDefault();
      closeManagePopup();
    }
  });

  deleteBtn.addEventListener("click", () => {
    const userPreset = currentUserPreset();
    if (!userPreset) return;
    userPresets = userPresets.filter((preset) => preset.id !== userPreset.id);
    saveUserPresets(userPresets);
    store.patch({ tonePreset: "custom" });
    rebuildPresetOptions();
    syncManagePopup();
    showPresetStatus("status.tonePreset.saved");
  });

  // Export button.
  const exportBtn = document.createElement("button");
  exportBtn.type = "button";
  exportBtn.className = "btn tone-preset-btn";
  exportBtn.dataset["i18n"] = "label.tonePreset.export";
  exportBtn.textContent = t("label.tonePreset.export");

  syncExportEnabled = (): void => {
    exportBtn.disabled = false;
  };
  syncExportEnabled();

  exportBtn.addEventListener("click", () => {
    downloadPresetAsBundle(selectedPresetForBundle());
  });

  function snapshotEffectivePresetPatch(): Partial<DemoState> {
    const state = store.get();
    const effective = effectiveDialValues(state);
    const effectiveState: DemoState = {
      ...state,
      ...effective,
      matrixSize: effective.matrixSize as DemoState["matrixSize"],
    };
    return snapshotPresetPatch(effectiveState);
  }

  function activeOverrideSnapshot(state: DemoState): MethodSnapshot | null {
    if (state.activeStackUuid !== null) {
      return (
        state.regionStacks.find((stack) => stack.uuid === state.activeStackUuid)
          ?.override ?? null
      );
    }
    const activeUuid = state.activeRegionUuids[0];
    if (activeUuid === undefined) return null;
    return (
      state.regionEntries.find((entry) => entry.uuid === activeUuid)?.override ??
      null
    );
  }

  function selectedPresetForBundle(): {
    name: string;
    preset: MethodSnapshot;
    assets: ReturnType<typeof embeddedAssetsForMethodSnapshot>;
  } {
    const preset = findPresetById(presetSelect.value);
    if (preset && presetSelect.value !== "custom") {
      const stateForPreset: DemoState = {
        ...createInitialState(),
        ...preset.patch,
      };
      return {
        name: preset.name,
        preset: buildMethodSnapshot(stateForPreset),
        assets: [],
      };
    }

    const state = store.get();
    const activeSnapshot = activeOverrideSnapshot(state);
    if (activeSnapshot !== null) {
      return {
        name: t("preset.custom.name"),
        preset: activeSnapshot,
        assets: embeddedAssetsForMethodSnapshot(state, activeSnapshot),
      };
    }

    const effective = effectiveDialValues(state);
    let effectiveState: DemoState = {
      ...state,
      ...effective,
      matrixSize: effective.matrixSize as DemoState["matrixSize"],
    };
    let patternRefId: PatternAssetId | null = null;
    let stampMaskRefId: StampMaskAssetId | null = null;

    if (effectiveState.family === "B" && effectiveState.patternImage) {
      const patternResult = registerAsset(
        effectiveState.assetLibrary,
        effectiveState.assetData,
        effectiveState.assetDataAccessOrder,
        effectiveState.patternImage,
        "pattern",
      );
      effectiveState = {
        ...effectiveState,
        assetLibrary: patternResult.assetLibrary,
        assetData: patternResult.assetData,
        assetDataAccessOrder: patternResult.assetDataAccessOrder,
      };
      patternRefId = patternResult.asset.id as PatternAssetId;
    }

    const needsStampMask =
      (effectiveState.family !== "B" && effectiveState.stampType === "custom") ||
      (effectiveState.family === "B" &&
        effectiveState.patternHalftoneStampType === "custom");
    if (needsStampMask && effectiveState.customStampMask) {
      const stampResult = registerAsset(
        effectiveState.assetLibrary,
        effectiveState.assetData,
        effectiveState.assetDataAccessOrder,
        effectiveState.customStampMask,
        "stampMask",
      );
      effectiveState = {
        ...effectiveState,
        assetLibrary: stampResult.assetLibrary,
        assetData: stampResult.assetData,
        assetDataAccessOrder: stampResult.assetDataAccessOrder,
      };
      stampMaskRefId = stampResult.asset.id as StampMaskAssetId;
    }

    const snapshot = buildMethodSnapshot(
      effectiveState,
      stampMaskRefId,
      patternRefId,
    );
    return {
      name: t("preset.custom.name"),
      preset: snapshot,
      assets: embeddedAssetsForMethodSnapshot(effectiveState, snapshot),
    };
  }

  function applyImportedMethodSnapshot(
    id: string,
    snapshot: MethodSnapshot,
    assetPatch: Pick<
      DemoState,
      "assetLibrary" | "assetData" | "assetDataAccessOrder"
    >,
  ): void {
    const state = store.get();
    const nextUndoStack = pushUndoSnapshot(state);

    if (state.activeStackUuid !== null) {
      const stackUuid = state.activeStackUuid;
      store.patch({
        ...assetPatch,
        regionStacks: state.regionStacks.map((stack) =>
          stack.uuid === stackUuid ? { ...stack, override: snapshot } : stack,
        ),
        tonePreset: "custom",
        regionUndoStack: nextUndoStack,
      });
      return;
    }

    if (state.activeRegionUuids.length > 0) {
      const active = new Set(state.activeRegionUuids);
      store.patch({
        ...assetPatch,
        regionEntries: state.regionEntries.map((entry) =>
          active.has(entry.uuid) ? { ...entry, override: snapshot } : entry,
        ),
        tonePreset: "custom",
        regionUndoStack: nextUndoStack,
      });
      return;
    }

    store.patch({
      ...assetPatch,
      ...methodSnapshotToPresetPatch(snapshot),
      ...binaryPatchForMethodSnapshot(snapshot, assetPatch.assetData),
      tonePreset: id,
      regionUndoStack: nextUndoStack,
    });
  }

  function importPreset(validated: {
    name: string;
    patch: Partial<DemoState>;
  }): void {
    const newPreset: UserPreset = {
      id: generateId(),
      name: validated.name,
      patch: validated.patch,
    };
    userPresets = [...userPresets, newPreset];
    saveUserPresets(userPresets);
    applyTonePresetPatch(newPreset.id, newPreset.patch);
    rebuildPresetOptions();
  }

  function importPresetBundle(rawPayload: string | ArrayBuffer): boolean {
    try {
      const bundle = decodePresetBundle(rawPayload);
      const patch = methodSnapshotToPresetPatch(bundle.preset);
      const newPreset: UserPreset = {
        id: generateId(),
        name: bundle.name ?? t("preset.custom.name"),
        patch,
      };
      userPresets = [...userPresets, newPreset];
      saveUserPresets(userPresets);
      const assetPatch = mergeEmbeddedAssetsIntoState(
        store.get(),
        bundle.assets,
        bundle.assetData,
      );
      applyImportedMethodSnapshot(newPreset.id, bundle.preset, assetPatch);
      rebuildPresetOptions();
      return true;
    } catch {
      return false;
    }
  }

  // Import button.
  const importBtn = document.createElement("button");
  importBtn.type = "button";
  importBtn.className = "btn tone-preset-btn";
  importBtn.dataset["i18n"] = "label.tonePreset.import";
  importBtn.textContent = t("label.tonePreset.import");

  const importFileLabel = document.createElement("label");
  importFileLabel.htmlFor = "ctrl-tonePresetImport";
  importFileLabel.className = "sr-only";
  importFileLabel.textContent = t("label.tonePreset.import");
  presetActionsRow.appendChild(importFileLabel);

  const importFileInput = document.createElement("input");
  importFileInput.type = "file";
  importFileInput.id = "ctrl-tonePresetImport";
  importFileInput.accept = ".amtp,.json,application/json,application/zip";
  importFileInput.className = "tone-preset-import-input";
  importFileInput.setAttribute("aria-hidden", "true");
  importFileInput.tabIndex = -1;

  importBtn.addEventListener("click", () => {
    importFileInput.value = "";
    importFileInput.click();
  });

  importFileInput.addEventListener("change", () => {
    const file = importFileInput.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.addEventListener("load", () => {
      try {
        const payload = reader.result as ArrayBuffer;
        if (importPresetBundle(payload)) {
          showPresetStatus("status.tonePreset.saved");
          return;
        }
        const text = new TextDecoder().decode(payload);
        const raw = JSON.parse(text);
        const validated = validateImportedPreset(raw);
        if (!validated) {
          showPresetStatus("status.tonePreset.invalidFile", 3000);
          return;
        }
        importPreset(validated);
        showPresetStatus("status.tonePreset.saved");
      } catch {
        showPresetStatus("status.tonePreset.invalidFile", 3000);
      }
    });
    reader.readAsArrayBuffer(file);
  });

  presetActionsRow.appendChild(saveBtn);
  presetActionsRow.appendChild(saveNameInput);
  presetActionsRow.appendChild(manageBtn);
  presetActionsRow.appendChild(importFileInput);
  presetActionsRow.appendChild(saveStatusEl);
  manageActionSection.appendChild(deleteBtn);
  manageActionSection.appendChild(exportBtn);
  manageActionSection.appendChild(importBtn);
  presetRow.appendChild(presetActionsRow);

  tonePresetSection.body.appendChild(presetRow);

  // Build initial options (after root is set, after makeCustomSelect).
  rebuildPresetOptions();

  presetSelect.addEventListener("change", () => {
    const id = presetSelect.value;
    syncExportEnabled();
    if (id === "custom") {
      store.patch({ tonePreset: "custom" });
      return;
    }
    // Check user presets first.
    const userPreset = userPresets.find((p) => p.id === id);
    if (userPreset) {
      applyTonePresetPatch(id, userPreset.patch);
      return;
    }
    const preset = TONE_PRESETS.find((p) => p.id === id);
    if (!preset?.patch) return;
    applyTonePresetPatch(id, preset.patch);
  });


  function syncPresetCrudI18n(): void {
    saveNameInput.placeholder = t("label.tonePreset.name.placeholder");
    renameInput.placeholder = t("label.tonePreset.name.placeholder");
    importFileLabel.textContent = t("label.tonePreset.import");
    saveNameLabel.textContent = t("label.tonePreset.save");
    syncTonePresetTitle();
    syncManagePopup();
  }
  syncPresetCrudI18n();
  onLocaleChange(syncPresetCrudI18n);

  return { presetSelect, syncExportEnabled };
}
